'use strict'

import {
  Badge,
  CircularProgress,
  Collapse,
  Popover,
  Switch,
  Tooltip
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import FilterListIcon from '@material-ui/icons/FilterList'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SkipNextIcon from '@material-ui/icons/SkipNext'
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import StarIcon from '@material-ui/icons/Star'
import WarningIcon from '@material-ui/icons/Warning'
import VisibilityIcon from '@material-ui/icons/Visibility'
import { getCrewColor, getRouteColor } from './crewColors'
import { parseYmd, toYmd, addDays, getWeekTypeForDate } from './scheduleUtils'

const PLANNING_MODES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'two-week', label: '2-Wk' }
]

/**
 * Floating glass-pill toolbar positioned over the map.
 * Group 1: Route selector + date nav + Day/Week/2-Wk segmented control
 * Group 2: VIP + Unassigned + Legend toggles + filter popover + refresh
 */
export default function MapToolbar ({
  isRouteMode, isFleetMode,
  focusCrewId, setFocusCrewId,
  focusRouteId, setFocusRouteId,
  focusDate, setFocusDate,
  planningMode, setPlanningMode,
  contextWeekType, setContextWeekType,
  availableRoutes, availableCrews,
  showFleetRoutes, setShowFleetRoutes,
  fleetRoutesLoading,
  showRouteStops, setShowRouteStops,
  showUnassigned, setShowUnassigned,
  fleetTodayOnly, setFleetTodayOnly,
  showIrregularMtc, setShowIrregularMtc,
  routeFilterId, setRouteFilterId,
  resetMapControls,
  filterAnchorEl, setFilterAnchorEl,
  showVipOnly, setShowVipOnly,
  showAnchorsOnly, setShowAnchorsOnly,
  showLockedOnly, setShowLockedOnly,
  showCentroids, setShowCentroids,
  showDueOnly, setShowDueOnly,
  frequencyFocus, setFrequencyFocus,
  activeSecondaryFilterCount,
  fetchMapData, loading,
  loadingMetrics, loadingFocusContextStats,
  loadingFocusedRoute,
  propertyFeatures,
  legendOpen, setLegendOpen,
  routeLegend, routeIds, crewIds, focusRouteFromChip,
  reviewNavOpen, setReviewNavOpen,
  reviewCombos, filteredReviewCombos,
  reviewComboIdx, setReviewComboIdx,
  reviewNavNext, reviewNavPrev,
  navigateToCombo,
  approvedRouteDays, skipApproved, setSkipApproved,
  selectedScenarioId
}) {
  const anyLoading = loading || loadingMetrics || loadingFocusContextStats || loadingFocusedRoute || fleetRoutesLoading
  const weekType = getWeekTypeForDate(parseYmd(focusDate))

  const handleRouteChange = (e) => {
    const next = String(e.target.value || '')
    if (next === '__ALL__') {
      setFocusRouteId('__ALL__')
      setFocusCrewId('')
      return
    }
    if (isRouteMode) {
      setFocusRouteId(next)
      const route = availableRoutes.find(r => r.routeId === next)
      setFocusCrewId(route?.crewId || '')
    } else {
      setFocusCrewId(next)
    }
  }

  const navigateDate = (direction) => {
    const cur = parseYmd(focusDate)
    const day = cur.getDay()
    let offset
    if (direction === 'prev') {
      offset = day === 1 ? -3 : day === 0 ? -2 : -1
    } else {
      offset = day === 5 ? 3 : day === 6 ? 2 : 1
    }
    setFocusDate(toYmd(addDays(cur, offset)))
  }

  return (
    <>
      {/* ── Floating toolbar ─────────────────────────────────── */}
      <div style={{
        position: 'absolute', top: 14, left: 'calc(50% - 188px)',
        transform: 'translateX(-50%)', zIndex: 110,
        display: 'flex', gap: 6, alignItems: 'center'
      }}>
        {/* Group 1: Route + Date + Mode */}
        <div className="toolbar-pill">
          <select
            value={isRouteMode ? focusRouteId : focusCrewId}
            onChange={handleRouteChange}
            style={{
              padding: '4px 8px', borderRadius: 5,
              border: '1px solid var(--df-line, #e0d8c2)',
              fontSize: 12, fontFamily: 'inherit', outline: 'none',
              color: 'var(--df-ink-2, #3d463a)', background: 'transparent',
              cursor: 'pointer', minWidth: 130
            }}
          >
            <option value="">Focus {isRouteMode ? 'route' : 'crew'}</option>
            <option value="__ALL__">All Routes (Fleet)</option>
            {isRouteMode
              ? availableRoutes.map(route => (
                <option key={route.routeId} value={route.routeId}>{route.routeName}</option>
              ))
              : availableCrews.map(crew => (
                <option key={crew.crewId} value={crew.crewId}>{crew.crewName}</option>
              ))
            }
          </select>

          {isFleetMode && (
            <span style={{
              padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
              background: 'var(--df-primary-soft, #e3eee0)', color: 'var(--df-primary, #1f5e3a)',
              whiteSpace: 'nowrap'
            }}>FLEET</span>
          )}

          <div className="toolbar-divider" />

          <Tooltip title="Previous business day" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => navigateDate('prev')}
            >
              <ChevronLeftIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          <input
            type="date"
            value={focusDate}
            onChange={(e) => setFocusDate(String(e.target.value || toYmd(new Date())))}
            style={{
              padding: '4px 8px', borderRadius: 5,
              border: '1px solid var(--df-line, #e0d8c2)',
              fontSize: 12, fontFamily: 'inherit', outline: 'none',
              color: 'var(--df-ink-2, #3d463a)', background: 'transparent'
            }}
          />

          <Tooltip title="Next business day" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => navigateDate('next')}
            >
              <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          <span style={{
            fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            background: weekType === 'A' ? '#dde9f5' : '#f5dde2',
            color: weekType === 'A' ? '#1565c0' : '#c62828',
            whiteSpace: 'nowrap'
          }}>Wk {weekType}</span>

          <div className="toolbar-divider" />

          {/* Segmented control: Day / Week / 2-Wk */}
          <div className="seg-control" style={{ padding: 2 }}>
            {PLANNING_MODES.map(m => (
              <button
                key={m.value}
                className={'seg-btn' + (planningMode === m.value ? ' active' : '')}
                onClick={() => {
                  setPlanningMode(m.value)
                  if (m.value === 'two-week') setContextWeekType('ALL')
                }}
                style={{ padding: '3px 9px', fontSize: 11 }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Group 2: Toggles + filters */}
        <div className="toolbar-pill">
          <Tooltip title="VIP properties only" arrow>
            <button
              className={'toolbar-icon-btn' + (showVipOnly ? ' active' : '')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: showVipOnly ? 'var(--df-primary-soft, #e3eee0)' : 'transparent',
                color: showVipOnly ? 'var(--df-primary, #1f5e3a)' : 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => setShowVipOnly(prev => !prev)}
            >
              <StarIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          <Tooltip title={showUnassigned ? 'Hide unassigned' : 'Show unassigned'} arrow>
            <button
              className={'toolbar-icon-btn' + (showUnassigned ? ' active' : '')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: showUnassigned ? 'var(--df-primary-soft, #e3eee0)' : 'transparent',
                color: showUnassigned ? 'var(--df-primary, #1f5e3a)' : 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => {
                if (routeFilterId === 'unassigned') setRouteFilterId('')
                setShowUnassigned(prev => !prev)
              }}
            >
              <WarningIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          <div style={{ width: 1, height: 20, background: 'var(--df-line, #e0d8c2)', margin: '0 2px', flexShrink: 0 }} />

          <Tooltip title={legendOpen ? 'Hide legend' : 'Show legend'} arrow>
            <button
              className={'toolbar-icon-btn' + (legendOpen ? ' active' : '')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: legendOpen ? 'var(--df-primary-soft, #e3eee0)' : 'transparent',
                color: legendOpen ? 'var(--df-primary, #1f5e3a)' : 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => setLegendOpen(prev => !prev)}
            >
              <VisibilityIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          <Tooltip title="Map filters and layers" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: activeSecondaryFilterCount > 0 ? 'var(--df-primary-soft, #e3eee0)' : 'transparent',
                color: activeSecondaryFilterCount > 0 ? 'var(--df-primary, #1f5e3a)' : 'var(--df-ink-4, #97a094)',
                position: 'relative'
              }}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            >
              <Badge badgeContent={activeSecondaryFilterCount} color="secondary" overlap="rectangular">
                <FilterListIcon style={{ fontSize: 18 }} />
              </Badge>
            </button>
          </Tooltip>

          <Tooltip title="Refresh map data" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: 'transparent',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={fetchMapData}
              disabled={loading}
            >
              {anyLoading
                ? <CircularProgress size={16} style={{ color: 'var(--df-primary, #1f5e3a)' }} />
                : <RefreshIcon style={{ fontSize: 18 }} />
              }
            </button>
          </Tooltip>

          {/* Visible count + context chip */}
          <span style={{
            fontSize: 10.5, fontWeight: 600, color: 'var(--df-ink-4, #97a094)',
            whiteSpace: 'nowrap', padding: '0 4px'
          }}>
            {propertyFeatures.length} props
          </span>
        </div>

        {/* Group 3: Review button (only when scenario + combos exist) */}
        {!!selectedScenarioId && reviewCombos.length > 0 && (
          <div className="toolbar-pill">
            <Tooltip title={reviewNavOpen ? 'Close review navigator' : 'Step through each combo to approve'} arrow>
              <button
                className={'toolbar-icon-btn' + (reviewNavOpen ? ' active' : '')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  width: 'auto', padding: '4px 10px', borderRadius: 6, border: 'none',
                  cursor: 'pointer', transition: 'all 0.12s', fontFamily: 'inherit',
                  fontSize: 11.5, fontWeight: 600,
                  background: reviewNavOpen ? 'var(--df-primary, #1f5e3a)' : 'transparent',
                  color: reviewNavOpen ? '#fff' : 'var(--df-ink-4, #97a094)'
                }}
                onClick={() => {
                  setReviewNavOpen(prev => !prev)
                  if (!reviewNavOpen && filteredReviewCombos.length) {
                    navigateToCombo(filteredReviewCombos[reviewComboIdx])
                  }
                }}
              >
                <CheckCircleIcon style={{ fontSize: 16 }} />
                Review ({approvedRouteDays.length}/{reviewCombos.length})
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Filter popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{
          padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
          minWidth: 220, background: 'var(--df-surface, #fbf8f1)'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showVipOnly} onChange={(e) => setShowVipOnly(e.target.checked)} size="small" />
            VIP only
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showAnchorsOnly} onChange={(e) => setShowAnchorsOnly(e.target.checked)} size="small" />
            Anchors only
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showLockedOnly} onChange={(e) => setShowLockedOnly(e.target.checked)} size="small" />
            Locked only
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showCentroids} onChange={(e) => setShowCentroids(e.target.checked)} size="small" />
            Centroids
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showDueOnly} onChange={(e) => setShowDueOnly(e.target.checked)} size="small" />
            Due in context
          </label>

          {isFleetMode && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
                <Switch checked={fleetTodayOnly} onChange={(e) => setFleetTodayOnly(e.target.checked)} size="small" />
                Today&apos;s stops only
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
                <Switch checked={showFleetRoutes} onChange={(e) => setShowFleetRoutes(e.target.checked)} size="small" />
                Route lines
              </label>
            </>
          )}

          {!isFleetMode && focusCrewId && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
              <Switch checked={showRouteStops} onChange={(e) => setShowRouteStops(e.target.checked)} size="small" />
              Focused route only
            </label>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showIrregularMtc} onChange={(e) => setShowIrregularMtc(e.target.checked)} size="small" />
            Irregular maintenance
          </label>

          <div style={{ height: 1, background: 'var(--df-line, #e0d8c2)', margin: '4px 0' }} />

          <select
            value={contextWeekType}
            onChange={(e) => setContextWeekType(String(e.target.value || 'ALL'))}
            style={{
              padding: '5px 8px', borderRadius: 5, border: '1px solid var(--df-line, #e0d8c2)',
              fontSize: 12, fontFamily: 'inherit', color: 'var(--df-ink-2, #3d463a)',
              background: 'transparent', cursor: 'pointer', width: '100%'
            }}
          >
            <option value="ALL">Any Week</option>
            <option value="A">Week A</option>
            <option value="B">Week B</option>
          </select>

          <select
            value={frequencyFocus}
            onChange={(e) => setFrequencyFocus(String(e.target.value || 'all'))}
            style={{
              padding: '5px 8px', borderRadius: 5, border: '1px solid var(--df-line, #e0d8c2)',
              fontSize: 12, fontFamily: 'inherit', color: 'var(--df-ink-2, #3d463a)',
              background: 'transparent', cursor: 'pointer', width: '100%'
            }}
          >
            <option value="all">All Freq</option>
            <option value="weekly">Not Bi-Weekly</option>
            <option value="biweekly">Bi-Weekly</option>
          </select>

          <button
            onClick={resetMapControls}
            disabled={activeSecondaryFilterCount === 0}
            style={{
              padding: '6px 10px', borderRadius: 6, border: '1px solid var(--df-line, #e0d8c2)',
              background: 'transparent', cursor: activeSecondaryFilterCount === 0 ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              color: activeSecondaryFilterCount === 0 ? 'var(--df-ink-4, #97a094)' : 'var(--df-ink-2, #3d463a)',
              opacity: activeSecondaryFilterCount === 0 ? 0.5 : 1
            }}
          >
            Reset Filters
          </button>
        </div>
      </Popover>

      {/* ── Review navigator bar (below toolbar, floating) ─── */}
      <Collapse in={reviewNavOpen && filteredReviewCombos.length > 0}>
        <div style={{
          position: 'absolute', top: 56, left: '50%', transform: 'translateX(-50%)',
          zIndex: 110, display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', borderRadius: 'var(--df-radius, 10px)',
          background: 'rgba(251,248,241,0.96)', backdropFilter: 'blur(10px)',
          border: '1px solid var(--df-line-2, #d1c8ac)',
          boxShadow: '0 4px 12px rgba(28,36,25,0.10)'
        }}>
          <Tooltip title="Previous combo" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={reviewNavPrev}
              disabled={filteredReviewCombos.length <= 1}
            >
              <SkipPreviousIcon style={{ fontSize: 16 }} />
            </button>
          </Tooltip>

          {(() => {
            const combo = filteredReviewCombos[reviewComboIdx]
            if (!combo) return null
            const wtColor = combo.weekType === 'A' ? '#1565c0' : '#c62828'
            const wtBg = combo.weekType === 'A' ? '#dde9f5' : '#f5dde2'
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {combo.approved
                  ? <CheckCircleIcon style={{ color: '#2f7a3e', fontSize: 16 }} />
                  : <RadioButtonUncheckedIcon style={{ color: 'var(--df-ink-4, #97a094)', fontSize: 16 }} />
                }
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--df-ink, #1c2419)', whiteSpace: 'nowrap' }}>
                  {combo.routeName || combo.crewId}
                </span>
                <span style={{
                  fontSize: 10.5, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                  background: 'var(--df-bg-2, #ece5d3)', color: 'var(--df-ink-3, #6f7a6b)'
                }}>{combo.dayName.slice(0, 3)}</span>
                <span style={{
                  fontSize: 10.5, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                  background: wtBg, color: wtColor
                }}>Wk {combo.weekType}</span>
              </div>
            )
          })()}

          <Tooltip title="Next combo" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={reviewNavNext}
              disabled={filteredReviewCombos.length <= 1}
            >
              <SkipNextIcon style={{ fontSize: 16 }} />
            </button>
          </Tooltip>

          <div style={{ width: 1, height: 16, background: 'var(--df-line, #e0d8c2)', flexShrink: 0 }} />

          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3, #6f7a6b)', whiteSpace: 'nowrap' }}>
            {reviewComboIdx + 1} / {filteredReviewCombos.length}
          </span>

          <button
            onClick={() => { setSkipApproved(prev => !prev); setReviewComboIdx(0) }}
            style={{
              padding: '3px 8px', borderRadius: 5, border: 'none',
              cursor: 'pointer', fontSize: 10.5, fontWeight: 600, fontFamily: 'inherit',
              background: skipApproved ? 'var(--df-primary, #1f5e3a)' : 'var(--df-bg-2, #ece5d3)',
              color: skipApproved ? '#fff' : 'var(--df-ink-3, #6f7a6b)',
              transition: 'all 0.12s'
            }}
          >
            {skipApproved ? 'Unapproved only' : 'Show all'}
          </button>

          <span style={{ fontSize: 11, fontWeight: 600, color: '#2f7a3e', whiteSpace: 'nowrap' }}>
            {approvedRouteDays.length} approved
          </span>
        </div>
      </Collapse>

      {/* ── Legend overlay (top-left, below toolbar) ────────── */}
      {legendOpen && (
        <div style={{
          position: 'absolute', top: 64, left: 16, zIndex: 110,
          background: 'var(--df-surface, #fbf8f1)',
          border: '1px solid var(--df-line, #e0d8c2)',
          borderRadius: 10, padding: '12px 14px',
          boxShadow: '0 8px 24px rgba(28,36,25,0.14)',
          display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180,
          animation: 'df-fadeIn 0.15s ease'
        }}>
          {routeLegend.map(([id, name]) => (
            <div
              key={id}
              onClick={() => focusRouteFromChip(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                opacity: routeFilterId && String(routeFilterId) !== String(id) ? 0.4 : 1,
                transition: 'opacity 0.15s'
              }}
            >
              <span style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                backgroundColor: getRouteColor(id, routeIds) || getCrewColor(id, crewIds)
              }} />
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--df-ink-2, #3d463a)' }}>
                {name}
              </span>
            </div>
          ))}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            paddingTop: 6, borderTop: '1px solid var(--df-line, #ede6d2)',
            cursor: 'pointer',
            opacity: routeFilterId && routeFilterId !== 'unassigned' ? 0.4 : 1,
            transition: 'opacity 0.15s'
          }}
            onClick={() => {
              const next = routeFilterId === 'unassigned' ? '' : 'unassigned'
              resetMapControls()
              setRouteFilterId(next)
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#cec3a5', flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: '#a9341d' }}>Unassigned</span>
          </div>
        </div>
      )}
    </>
  )
}
